import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAccessToken,
  clearAccessToken,
  getQuestions,
  getUserAnswers,
  saveAnswer,
  updateAnswer,
  submitFragebogen,
} from "../utils/fetchFunctions";
import { getUserRole } from "../utils/jwtUtils";
import FormContainer from "../components/FormContainer";
import Button from "../components/Button";
import FormError from "../components/FormError";
import "./fragebogen.css";

/**
 * @description Main questionnaire component that displays questions, handles answer submission, and manages filtering.
 * Updated to support point-based multiple-choice options.
 */
const Fragenbogen = () => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState("");
  const [showMidSaveModal, setShowMidSaveModal] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isReferencesOpen, setIsReferencesOpen] = useState(false);
  const fetchingRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [filterDimension, setFilterDimension] = useState(
    location.state?.filterDimension || ""
  );

  useEffect(() => {
    if (fetchingRef.current) return;

    const token = getAccessToken();
    if (!token) {
      navigate("/login");
      return;
    }

    const role = getUserRole();
    setUserRole(role);

    const loadData = async () => {
      fetchingRef.current = true;
      setLoading(true);
      setError("");

      try {
        const questionsData = await getQuestions();
        setQuestions(questionsData);

        if (role !== "admin") {
          const answersData = await getUserAnswers().catch(() => []);
          const answersMap = {};
          answersData.forEach((answer) => {
            // Ensure we are storing the answer as a number
            answersMap[answer.question_id] = Number(answer.answer_value);
          });
          setAnswers(answersMap);
        }
      } catch (err) {
        if (err.message && err.message.includes("401")) {
          clearAccessToken();
          navigate("/login");
          return;
        }
        setError(err.message || "Error loading data");
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    loadData();
  }, [navigate]);

  /**
   * @description Handles answer change for a question. Saves new point value or updates existing one via API.
   */
  const handleAnswerChange = async (questionId, pointsValue) => {
    const questionIdNum = parseInt(questionId, 10);
    setSaving((prev) => ({ ...prev, [questionIdNum]: true }));

    try {
      const existingAnswer = answers[questionIdNum] !== undefined;

      if (existingAnswer) {
        await updateAnswer(questionIdNum, pointsValue);
      } else {
        await saveAnswer(questionIdNum, pointsValue);
      }

      setAnswers((prev) => ({
        ...prev,
        [questionIdNum]: pointsValue,
      }));
    } catch (err) {
      setError(err.message || "Error saving answer");
    } finally {
      setSaving((prev) => {
        const updated = { ...prev };
        delete updated[questionIdNum];
        return updated;
      });
    }
  };

  /**
   * @description Filters questions based on selected dimension.
   */
  const getFilteredQuestions = () => {
    return questions.filter((question) => {
      if (
        filterDimension &&
        (!Array.isArray(question.dimensions) ||
          !question.dimensions.includes(filterDimension))
      ) {
        return false;
      }
      return true;
    });
  };

  /**
   * @description Groups filtered questions by their primary Dimension.
   */
  const groupQuestionsByDimension = () => {
    const filtered = getFilteredQuestions();
    const grouped = {};
    filtered.forEach((question) => {
      const primaryDimension = (question.dimensions && question.dimensions[0]) || "Other";
      if (!grouped[primaryDimension]) {
        grouped[primaryDimension] = [];
      }
      grouped[primaryDimension].push(question);
    });
    return grouped;
  };

  const getUniqueDimensions = () => {
    const dimensions = new Set();
    questions.forEach((q) => {
      if (Array.isArray(q.dimensions)) {
        q.dimensions.forEach((d) => dimensions.add(d));
      }
    });
    return Array.from(dimensions).sort();
  };

  const getDimensionMetadata = () => {
    const metadata = {};
    questions.forEach((question) => {
      const dimension = (question.dimensions && question.dimensions[0]) || "Other";
      if (!metadata[dimension]) {
        metadata[dimension] = {
          definition: question.definition || "",
          references: Array.isArray(question.references) ? question.references : [],
        };
      }
    });
    return metadata;
  };

  const saveAllAnswers = async () => {
    setIsSavingAll(true);
    setError("");

    try {
      const savePromises = Object.entries(answers).map(
        async ([questionId, answerValue]) => {
          const questionIdNum = parseInt(questionId, 10);
          try {
            await saveAnswer(questionIdNum, answerValue);
          } catch (err) {
            await updateAnswer(questionIdNum, answerValue);
          }
        }
      );

      await Promise.all(savePromises);
      setShowMidSaveModal(true);
    } catch (err) {
      setError(err.message || "Error saving draft");
    } finally {
      setIsSavingAll(false);
    }
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      const totalQuestions = questions.length;
      const answeredQuestions = Object.keys(answers).length;

      if (answeredQuestions < totalQuestions) {
        setError(
          "Please answer all questions before completing the questionnaire."
        );
        setIsSubmitting(false);
        return;
      }

      const savePromises = Object.entries(answers).map(
        async ([questionId, answerValue]) => {
          const questionIdNum = parseInt(questionId, 10);
          try {
            await saveAnswer(questionIdNum, answerValue);
          } catch (err) {
            await updateAnswer(questionIdNum, answerValue);
          }
        }
      );

      await Promise.all(savePromises);

      const result = await submitFragebogen();

      navigate("/dashboard/overview", {
        state: {
          dimensions: result.dimensions,
          levelProgress: result.levelProgress,
          blockingQuestions: result.blockingQuestions,
          overallLevel: result.overallLevel,
          isAllDimensionsComplete: result.isAllDimensionsComplete,
        },
      });
    } catch (err) {
      setError(err.message || "Error completing questionnaire");
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="fragebogen-page">
        <FormContainer className="fragebogen-container">
          <div className="fragebogen-loading">Loading questions...</div>
        </FormContainer>
      </main>
    );
  }

  if (error && !questions.length) {
    return (
      <main className="fragebogen-page">
        <FormContainer className="fragebogen-container">
          <FormError error={error} />
          <Button
            onClick={() => window.location.reload()}
            className="auth-button"
          >
            Try again
          </Button>
        </FormContainer>
      </main>
    );
  }

  const groupedQuestions = groupQuestionsByDimension();

  return (
    <main className="fragebogen-page">
      <FormContainer className="fragebogen-container">
        <h1 className="auth-title">Questionnaire</h1>
        <p className="auth-subtitle">
          {userRole === "admin"
            ? "Overview of all questions in the questionnaire (view mode)"
            : "Please select exactly one answer per question that best describes your current situation."}
        </p>
        
        {userRole !== "admin" && <div className="fragebogen-divider" />}

        {userRole !== "admin" && (
          <div className="fragebogen-info-wrapper">
            <div className="fragebogen-info-actions">
              <button
                type="button"
                className="fragebogen-info-toggle"
                onClick={() => setIsInfoOpen(!isInfoOpen)}
                aria-expanded={isInfoOpen}
                aria-controls="fragebogen-info-content"
              >
                {isInfoOpen ? "Hide hints" : "Show hints"}
              </button>
              <button
                type="button"
                className="fragebogen-info-toggle"
                onClick={() => setIsReferencesOpen(!isReferencesOpen)}
                aria-expanded={isReferencesOpen}
                aria-controls="fragebogen-references-content"
              >
                {isReferencesOpen ? "Hide References" : "Show References"}
              </button>
            </div>
            <div
              id="fragebogen-info-content"
              className={`fragebogen-info ${isInfoOpen ? "is-open" : ""}`}
            >
              <p>The questions are sorted by Dimension.</p>
              <p>
                Each option is worth a specific number of points. Your points will be 
                summed up at the end to calculate your overall Maturity Level for each dimension.
              </p>
              <p>
                The more honestly you answer the questions, the more accurate and helpful 
                the evaluation and recommendations for your organization will be.
              </p>
              <p>You can save the questionnaire as a draft at any time and continue later.</p>
            </div>
            <div
              id="fragebogen-references-content"
              className={`fragebogen-references ${isReferencesOpen ? "is-open" : ""}`}
            >
              {Object.entries(getDimensionMetadata()).map(([dimension, metadata]) => (
                <div key={dimension} className="fragebogen-reference-item">
                  <p className="fragebogen-reference-title">{dimension}</p>
                  <ul className="fragebogen-reference-list">
                    {metadata.references.map((reference) => (
                      <li key={reference}>{reference}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <FormError error={error} />}

        {userRole !== "admin" && (
          <div className="fragebogen-filters">
            <div className="fragebogen-filter">
              <label htmlFor="filter-dimension">Filter by Dimension</label>
              <select
                id="filter-dimension"
                value={filterDimension}
                onChange={(e) => setFilterDimension(e.target.value)}
              >
                <option value="">All Dimensions</option>
                {getUniqueDimensions().map((dimension) => (
                  <option key={dimension} value={dimension}>
                    {dimension}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="fragebogen-questions">
          {Object.entries(groupedQuestions).map(([dimension, dimQuestions]) => (
            <div key={dimension} className="fragebogen-area">
              <h2 className="fragebogen-area-title">{dimension}</h2>
              <p className="fragebogen-area-definition">
                {dimQuestions[0]?.definition}
              </p>
              {dimQuestions.map((question) => {
                const questionId = question.id;
                const currentAnswer = answers[questionId];
                const isSaving = saving[questionId];

                return (
                  <div key={questionId} className="fragebogen-question">
                    <div className="fragebogen-question-header">
                      <h3 className="fragebogen-question-title">
                        {question.question}
                      </h3>
                    </div>

                    {userRole !== "admin" && (
                      <div className="fragebogen-options-list">
                        {question.options.map((option, idx) => (
                          <label
                            key={idx}
                            className={[
                              "fragebogen-option",
                              currentAnswer === option.points ? "fragebogen-option--selected" : "",
                              isSaving ? "fragebogen-option--saving" : "",
                            ].filter(Boolean).join(" ")}
                          >
                            <input
                              type="radio"
                              name={`question-${questionId}`}
                              value={option.points}
                              checked={currentAnswer === option.points}
                              onChange={() => handleAnswerChange(questionId, option.points)}
                              disabled={isSaving}
                            />
                            <span className="fragebogen-option-text">{option.text}</span>
                            <span className="fragebogen-option-points">
                              {option.points} {option.points === 1 ? "pt." : "pts."}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {userRole !== "admin" && (
          <div className="fragebogen-actions">
            <Button
              onClick={saveAllAnswers}
              disabled={isSavingAll || isSubmitting}
              loading={isSavingAll}
              className="auth-button fragebogen-save-button"
            >
              Save Draft
            </Button>
            <Button
              onClick={handleFinalSubmit}
              disabled={isSavingAll || isSubmitting}
              loading={isSubmitting}
              className="auth-button fragebogen-submit-button"
            >
              Complete Questionnaire
            </Button>
          </div>
        )}
      </FormContainer>

      {showMidSaveModal &&
        createPortal(
          <div
            className="modal-overlay"
            onClick={() => setShowMidSaveModal(false)}
          >
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">Draft saved successfully</h2>
              <p className="modal-message">
                Your answers have been saved. You can safely leave this page.
              </p>
              <button
                className="modal-button"
                onClick={() => setShowMidSaveModal(false)}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}
    </main>
  );
};

export default Fragenbogen;